import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import SydModal from '../../commonDesign/SydModal'
import SydInput from '../../commonDesign/SydInput'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../commonDesign/SydLabel'
import SydButton from '../../commonDesign/Button'
import { useAuthorizeUserIntegration } from '../../../api/users'
import Alert from '../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../constants'

const IntegrationAuthDialog = ({
  integration = '',
  open,
  onClose,
  onSubmit: _onSubmit,
  title = 'Connect {integration}',
  subtitle = 'Please enter your credentials to connect {integration}.',
  inputs = {}
}) => {
  const form = useForm()
  const { handleSubmit, reset } = form

  const { mutate, isLoading, error } = useAuthorizeUserIntegration()
  const onSubmit = useCallback(async (credentials) => {
    return new Promise((resolve, reject) => {
      mutate({ integration, credentials }, {
        onSuccess: () => {
          _onSubmit?.()
          onClose?.()
        },
        onError: (err) => reject(err)
      })
    })
  }, [_onSubmit, integration, mutate, onClose])

  const handleClose = () => {
    if (isLoading) return
    reset()
    onClose()
  }

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      fullWidth
      title={title}
      subtitle={subtitle}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
          {!!error && (
            <Alert severity={ALERT_SEVERITY.error} variant={ALERT_VARIANT.standard}>
              Incorrect credentials. Please try again.
            </Alert>
          )}
          {Object.entries(inputs).map(([key, input]) => (
            <Controller
              key={key}
              name={key}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState }) => (
                <div>
                  <SydLabel label={input.label} required>
                    <SydInput
                      size='sm'
                      disabled={isLoading}
                      error={hookFormErrorAdapter(form, fieldState)}
                      type={input.type ?? 'password'}
                      {...field}
                    />
                  </SydLabel>
                </div>
              )}
            />
          ))}

          <SydModalActions>
            <SydButton onClick={handleClose} variant='secondary' disabled={isLoading}>Cancel</SydButton>
            <SydButton
              onClick={handleSubmit(onSubmit)}
              variant='primary'
              processing={isLoading}
              disabled={isLoading}
              type='submit'
            >
              {isLoading ? 'Saving...' : 'Connect'}
            </SydButton>
          </SydModalActions>
        </form>
      </FormProvider>
    </SydModal>
  )
}

IntegrationAuthDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  integration: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  inputs: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string
    })
  )
}

export default IntegrationAuthDialog
